import React from "react";
import * as THREE from "three";
import { SymbolType } from "../../../../../types/floorplannerTypes";
import CatalogStandardMaterial from "../../../materials/CatalogStandardMaterial";
import { FloorPlan3DMaterialSlot } from "../../../products/product3DTypes";
import {
  hasAssignedSlotMaterial,
  resolvePrimitiveSymbolPartMaterial,
  resolvePrimitiveSymbolPartSurfaceRef,
} from "../../../materials/primitiveSymbolFill";
import { useBuiltInMaterialSurfaceMappingsRevision } from "../../../materials/builtInMaterialSurfaceMappings";

type DoorSlotMaterialProps = {
  symbol: SymbolType;
  slot: FloorPlan3DMaterialSlot;
  /** Used when no slot assignment is present. */
  fallbackColor: THREE.ColorRepresentation;
  fallbackRoughness?: number;
  fallbackMetalness?: number;
  opacity?: number;
  transparent?: boolean;
  depthWrite?: boolean;
  side?: THREE.Side;
};

/**
 * Applies Ready / catalog materials on door parts the same way stairs and
 * furniture do — solid color when unset, albedo via CatalogStandardMaterial
 * when a slot assignment exists (textured oak/walnut no longer look identical).
 */
const DoorSlotMaterial: React.FC<DoorSlotMaterialProps> = ({
  symbol,
  slot,
  fallbackColor,
  fallbackRoughness = 0.64,
  fallbackMetalness = 0.03,
  opacity = 1,
  transparent = false,
  depthWrite = true,
  side,
}) => {
  useBuiltInMaterialSurfaceMappingsRevision();
  const slotAssigned = hasAssignedSlotMaterial(symbol, slot);
  const surfaceRef = resolvePrimitiveSymbolPartSurfaceRef(symbol, slot);
  // useFillColor=false, ignoreSymbolFillColor=true — Ready slot wins over plan fill.
  const partMaterial = resolvePrimitiveSymbolPartMaterial(
    symbol,
    slot,
    false,
    true,
  );
  const presetDefaults = partMaterial.builtIn
    ? {
        roughness: partMaterial.builtIn.roughness,
        metalness: partMaterial.builtIn.metalness,
        repeatScale: partMaterial.builtIn.repeatScale,
      }
    : undefined;

  if (slotAssigned && partMaterial.isGlass && partMaterial.builtIn) {
    return (
      <meshPhysicalMaterial
        color={partMaterial.color}
        roughness={surfaceRef?.roughness ?? partMaterial.builtIn.roughness}
        metalness={0}
        transmission={partMaterial.builtIn.transmission ?? 1}
        thickness={partMaterial.builtIn.thickness ?? 0.05}
        ior={partMaterial.builtIn.ior ?? 1.5}
        transparent
        opacity={opacity < 1 ? opacity : 1}
        depthWrite={depthWrite}
        side={side}
      />
    );
  }

  if (slotAssigned) {
    return (
      <CatalogStandardMaterial
        color={partMaterial.color}
        surfaceRef={surfaceRef}
        defaults={presetDefaults}
        opacity={opacity}
        transparent={transparent}
        depthWrite={depthWrite}
        side={side}
      />
    );
  }

  return (
    <meshStandardMaterial
      color={fallbackColor}
      roughness={fallbackRoughness}
      metalness={fallbackMetalness}
      opacity={opacity}
      transparent={transparent}
      depthWrite={depthWrite}
      side={side}
    />
  );
};

export default DoorSlotMaterial;
